import React from "react"
import { useParams, Link } from "react-router-dom"
import { MARKETING_CHANNELS } from "../config/marketingConfig"
import { buildListsendDashboardUrl } from "../utils/listsendAuth"
import { navigateToPressReleaseApp, isPressReleaseV2Enabled } from "../utils/pressReleaseAuth"
import useFeatureGates from "../hooks/useFeatureGates"
import Icon from "../components/marketing/heroVisuals/Icon"
import MetaAdPreview from "../components/marketing/heroVisuals/MetaAdPreview"
import GoogleSearchResult from "../components/marketing/heroVisuals/GoogleSearchResult"
import SocialPostPreview from "../components/marketing/heroVisuals/SocialPostPreview"
import EmailComposerPreview from "../components/marketing/heroVisuals/EmailComposerPreview"
import PressReleaseDocument from "../components/marketing/heroVisuals/PressReleaseDocument"

const DEMO_BOOKING_URL = "https://calendly.com/andre-vesta/vesta-new-channel-walkthrough"

// Email channel runs the same listsend per-company auth flow that the
// /marketing channel card uses. Press release links straight into the
// tool at /marketing/press_release_ai (the channel has no subscription
// gate) — unless the company holds the press-release-v2 FeatureGate, in
// which case it hands off to the external Press Release app (same token
// scheme as ListSend, see utils/pressReleaseAuth.js). Other channels use a plain "View All Channels" link back to
// the index.
const handleListsendNav = (e) => {
  e.preventDefault()
  const url = buildListsendDashboardUrl()
  if (!url) {
    // eslint-disable-next-line no-alert
    alert("Unable to generate company token. Please refresh the page and try again.")
    return
  }
  window.location.href = url
}

// Maps channel.productPage.heroVariant → the React component that renders
// the per-channel hero illustration. Adding a new channel means: extend
// marketingConfig.js + drop a component here.
const HERO_VISUALS = {
  "ad-preview":    MetaAdPreview,
  "google-search": GoogleSearchResult,
  "social-post":   SocialPostPreview,
  "email-preview": EmailComposerPreview,
  "press-release": PressReleaseDocument,
}

// Channel-specific accent tokens get pushed onto the root .mpp element as
// CSS custom properties. The rest of the SCSS (.mpp-hero, .mpp-features,
// .mpp-bestfor, .mpp-cta) reads var(--mpp-accent) etc. so a single stylesheet
// themes all 5 channels with no per-channel modifier classes.
const themeStyle = (pp) => ({
  "--mpp-accent": pp.accent,
  "--mpp-accent2": pp.accent2,
  "--mpp-gradient": `linear-gradient(180deg, ${pp.accent} 0%, ${pp.accent2} 100%)`,
  "--mpp-accent-soft": pp.accentSoft,
  "--mpp-accent-line": pp.accentLine,
  "--mpp-cta-glow": pp.ctaGlow,
  "--mpp-cta-shadow": pp.ctaShadow,
  "--mpp-aurora-tr": pp.auroraTR,
  "--mpp-aurora-bl": pp.auroraBL,
  "--mpp-text-gradient": pp.textGradient,
  "--mpp-best-for-bg": pp.bestForBg,
  "--mpp-best-for-shadow": pp.bestForShadow,
})

const ChannelProductPage = () => {
  const { slug } = useParams()
  const channel = Object.values(MARKETING_CHANNELS).find(c => c.slug === slug)
  const { featureGates } = useFeatureGates('company')
  const pressReleaseV2 = isPressReleaseV2Enabled(featureGates)

  if (!channel || !channel.productPage) {
    return (
      <div className="marketing">
        <div className="cts-container cpp-not-found">
          <h2>Channel not found</h2>
          <p>The channel you&apos;re looking for doesn&apos;t exist or isn&apos;t available.</p>
          <Link to="/marketing" className="cpp-btn cpp-btn--primary">Back to Channels</Link>
        </div>
      </div>
    )
  }

  const pp = channel.productPage
  const Visual = HERO_VISUALS[pp.heroVariant] || MetaAdPreview
  const isEmail = slug === "email_marketing_ai"
  const isPressRelease = slug === "press_release_ai"

  const SecondaryCta = ({ ghost }) => {
    const btnClass = `mpp-btn mpp-btn--lg ${ghost ? "mpp-btn--ghost" : "mpp-btn--secondary"}`

    if (isEmail) {
      return (
        <a href="#" onClick={handleListsendNav} className={btnClass}>
          Go to Email Marketing <Icon name="arrow-right" size={16}/>
        </a>
      )
    }

    if (isPressRelease && pressReleaseV2) {
      return (
        <a href="#" onClick={navigateToPressReleaseApp} className={btnClass}>
          Go to Press Release AI <Icon name="arrow-right" size={16}/>
        </a>
      )
    }

    if (isPressRelease) {
      return (
        <Link to="/marketing/press_release_ai" className={btnClass}>
          Go to Press Release AI <Icon name="arrow-right" size={16}/>
        </Link>
      )
    }

    return (
      <Link to="/marketing" className={btnClass}>
        View All Channels
      </Link>
    )
  }

  return (
    <div className="marketing">
      <div className="mpp" style={themeStyle(pp)}>
        {/* ── Hero ── */}
        <section className="mpp-hero">
          <div className="mpp-hero__aurora"/>
          <div className="mpp-hero__glow mpp-hero__glow--top"/>
          <div className="mpp-hero__glow mpp-hero__glow--bottom"/>
          <div className="mpp-hero__grid"/>

          <div className="mpp-wrap mpp-hero__nav">
            <Link to="/marketing" className="mpp-back-pill">
              <Icon name="arrow-left" size={14}/> All Channels
            </Link>
          </div>

          <div className="mpp-wrap mpp-hero__inner">
            <div className="mpp-hero__content">
              <span className="mpp-hero__badge">
                <span className="mpp-hero__badge-icon"><Icon name="sparkles" size={11} stroke={2.4}/></span>
                {pp.badge}
              </span>

              <h1 className="mpp-hero__title">
                {pp.hero.headlineStart}
                <span className="mpp-hero__title-gradient">{pp.hero.headlineSpan}</span>
                {pp.hero.headlineEnd}
              </h1>

              <p className="mpp-hero__sub">{pp.hero.sub}</p>

              <div className="mpp-hero__ctas">
                <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mpp-btn mpp-btn--white mpp-btn--lg">
                  Book a Demo <Icon name="arrow-right" size={16}/>
                </a>
                <SecondaryCta ghost/>
              </div>

              <div className="mpp-hero__trust">
                {pp.hero.trust.map((t, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="mpp-hero__trust-divider"/>}
                    <span className="mpp-hero__trust-item">
                      <Icon name="check" size={14} stroke={2.4}/> {t}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="mpp-hero__visual-col">
              <Visual/>
            </div>
          </div>
        </section>

        {/* ── Feature Grid ── */}
        <section className="mpp-features">
          <div className="mpp-wrap">
            <div className="mpp-features__header">
              <div className="mpp-eyebrow">{pp.features.eyebrow}</div>
              <h2 className="mpp-features__title">
                {pp.features.headlineStart}
                <span className="mpp-accent">{pp.features.headlineAccent}</span>
                {pp.features.headlineEnd}
              </h2>
              <p className="mpp-features__sub">{pp.features.intro}</p>
            </div>

            <div className="mpp-features__grid">
              {pp.features.cards.map((card, i) => (
                <div key={i} className="mpp-fcard">
                  <div className="mpp-fcard__hairline"/>
                  <div className="mpp-fcard__icon">
                    <Icon name={card.icon} size={22} stroke={2.2}/>
                  </div>
                  <h3 className="mpp-fcard__title">{card.title}</h3>
                  <p className="mpp-fcard__desc">{card.description}</p>
                  <ul className="mpp-fcard__bullets">
                    {card.bullets.map((b, j) => (
                      <li key={j}>
                        <span className="mpp-fcard__bullet-dot"><Icon name="check" size={10} stroke={3}/></span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Best For ── */}
        <section className="mpp-bestfor">
          <div className="mpp-wrap">
            <div className="mpp-bestfor__card">
              <div className="mpp-bestfor__grid-mask"/>
              <div className="mpp-bestfor__inner">
                <div className="mpp-bestfor__copy">
                  <div className="mpp-bestfor__eyebrow">{pp.bestForBlock.eyebrow}</div>
                  <h2 className="mpp-bestfor__headline">{pp.bestForBlock.headline}</h2>
                  <p className="mpp-bestfor__body">{pp.bestForBlock.body}</p>
                </div>
                <div className="mpp-bestfor__stats">
                  {pp.bestForBlock.stats.map((s, i) => (
                    <div key={i} className={`mpp-bestfor__stat ${i < pp.bestForBlock.stats.length - 1 ? "mpp-bestfor__stat--border" : ""}`}>
                      <div className="mpp-bestfor__stat-label">{s.k}</div>
                      <div className="mpp-bestfor__stat-right">
                        <div className="mpp-bestfor__stat-value">{s.v}</div>
                        <div className="mpp-bestfor__stat-sub">{s.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="mpp-cta">
          <div className="mpp-wrap">
            <div className="mpp-cta__card">
              <div className="mpp-cta__glow"/>
              <div className="mpp-cta__inner">
                <div className="mpp-eyebrow">{pp.cta.eyebrow}</div>
                <h2 className="mpp-cta__title">{pp.cta.headline}</h2>
                <p className="mpp-cta__sub">{pp.cta.body}</p>
                <div className="mpp-cta__buttons">
                  <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mpp-btn mpp-btn--gradient mpp-btn--lg">
                    Book a Demo <Icon name="arrow-right" size={16}/>
                  </a>
                  <SecondaryCta/>
                </div>
                <div className="mpp-cta__fine">
                  <Icon name="check" size={14} stroke={2.4}/> No commitment, just a quick look at what&apos;s possible.
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default ChannelProductPage
